import * as THREE from 'three';

export enum LayerType {
  SPHERE = 'sphere',
  ELEMENTS = 'elements',
}

export class LayerManager {
  private layers: Map<LayerType, THREE.Object3D>;
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.layers = new Map();
    this.initializeLayers();
  }

  private initializeLayers(): void {
    // Create sphere layer
    const sphereLayer = new THREE.Object3D();
    sphereLayer.name = LayerType.SPHERE;
    this.layers.set(LayerType.SPHERE, sphereLayer);
    this.scene.add(sphereLayer);

    // Create elements layer
    const elementsLayer = new THREE.Object3D();
    elementsLayer.name = LayerType.ELEMENTS;
    this.layers.set(LayerType.ELEMENTS, elementsLayer);
    this.scene.add(elementsLayer);
  }

  public getLayer(type: LayerType): THREE.Object3D {
    const layer = this.layers.get(type);
    if (!layer) {
      throw new Error(`Layer ${type} not found`);
    }
    return layer;
  }

  public addToLayer(type: LayerType, object: THREE.Object3D): void {
    const layer = this.getLayer(type);
    if (type === LayerType.SPHERE) {
      // For sphere, we want to keep it in the scene but track it in the layer
      layer.userData.sphere = object;
    } else {
      layer.add(object);
    }
  }

  public removeFromLayer(type: LayerType, object: THREE.Object3D): void {
    const layer = this.getLayer(type);
    if (type === LayerType.SPHERE) {
      // For sphere, just remove the reference
      layer.userData.sphere = null;
    } else {
      layer.remove(object);
    }
  }

  public clearLayer(type: LayerType): void {
    const layer = this.getLayer(type);
    if (type === LayerType.SPHERE) {
      layer.userData.sphere = null;
    } else {
      while (layer.children.length > 0) {
        layer.remove(layer.children[0]);
      }
    }
  }

  public setLayerVisibility(type: LayerType, visible: boolean): void {
    const layer = this.getLayer(type);
    if (type === LayerType.SPHERE && layer.userData.sphere) {
      layer.userData.sphere.visible = visible;
    } else {
      layer.visible = visible;
    }
  }

  public getLayerVisibility(type: LayerType): boolean {
    const layer = this.getLayer(type);
    if (type === LayerType.SPHERE && layer.userData.sphere) {
      return layer.userData.sphere.visible;
    }
    return layer.visible;
  }
}
